import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import { WasteSummary } from "@/interfaces/interfaces";
import { IProject } from "@/interfaces/project.interface";
import { IDocument, WasteFormat } from "@/interfaces/wastetype.interface";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const wasteManagementApi = createApi({
  reducerPath: "wasteManagementApi",
  baseQuery: BaseWithAuth(),
  tagTypes: ["waste-management-summary", "format-setting", "documents"],
  endpoints: (builder) => ({
    //General
    getSummaryReport: builder.query<WasteSummary, string>({
      query: (query) =>
        `workspace/${getWorkspace()}/waste-management/summary${query ? `?${query}` : ""}`,
      providesTags: ["waste-management-summary"],
      transformResponse: (response: APIResponse<WasteSummary>) =>
        response?.data,
    }),

    getSummaryProject: builder.query<GeneralResponse<IProject[]>, string>({
      query: (query) =>
        `workspace/${getWorkspace()}/waste-management/summary/project${query ? `?${query}` : ""}`,
      providesTags: ["waste-management-summary"],
      transformResponse: (response: GeneralResponse<IProject[]>) => response,
    }),

    /* Waste Management Settings */

    getWasteFormat: builder.query<WasteFormat, { query?: string }>({
      query: ({ query }) =>
        `workspace/${getWorkspace()}/waste-management/setting${query ?? ""}`,
      providesTags: ["format-setting"],
      transformResponse: (response: APIResponse<WasteFormat>) => response?.data,
    }),

    getDocuments: builder.query<
      GeneralResponse<IDocument[]>,
      { filter?: string }
    >({
      query: ({ filter }) =>
        `workspace/${getWorkspace()}/waste-management/setting/document${filter || ""}`,
      providesTags: ["documents"],
    }),

    getSingleDocument: builder.query<IDocument, string>({
      query: (id) =>
        `workspace/${getWorkspace()}/waste-management/setting/document/${id}`,
      providesTags: ["documents"],
      transformResponse: (response: APIResponse<IDocument>) => response?.data,
    }),

    mutateDocs: builder.mutation<
      object,
      {
        id?: string;
        data?: FormData;
        params?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ id, data, params, method }) => ({
        url: `workspace/${getWorkspace()}/waste-management/setting/document/${id ?? ""}/${params ?? ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["documents"],
    }),

    // url eg :settings/{document/format} (POST/PATCH)
    alterWasteFormat: builder.mutation<
      object,
      {
        data: unknown;
        params?: string;
        method?: REQUEST_METHODS.PATCH | REQUEST_METHODS.POST;
      }
    >({
      query: ({ data, params, method = REQUEST_METHODS.PATCH }) => ({
        url: `workspace/${getWorkspace()}/waste-management/setting/${params ? params : ""}`,
        method: method.toUpperCase(),
        body: data,
      }),
      invalidatesTags: ["format-setting"],
    }),
  }),
});

export const {
  useGetSummaryReportQuery,
  useGetSummaryProjectQuery,
  useGetWasteFormatQuery,
  useGetDocumentsQuery,
  useGetSingleDocumentQuery,
  useMutateDocsMutation,
  useAlterWasteFormatMutation,
} = wasteManagementApi;
